// Basketball board data — reads the real ingested slate (get_basketball_board
// RPC): upcoming basketball fixtures + their current Pinnacle moneyline/spread/
// total. HONEST: real odds only; empty when nothing is ingested. It carries NO
// model price or edge (the BFPE hasn't run) — the UI shows odds, not picks.

import { useMemo } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

interface RawMarket {
  canonical_code: string;
  line_value: number | null;
  odds: number | null;
  book: string | null;
}
interface RawGame {
  fixture_id: string;
  home_team: string | null;
  away_team: string | null;
  league: string | null;
  tipoff_at: string | null;
  markets: RawMarket[];
}

export interface BballSide { line: number | null; odds: number | null }
export interface BballGame {
  fixtureId: string;
  home: string;
  away: string;
  league: string | null;
  tipoffAt: string | null;
  moneyline: { home: number | null; away: number | null };
  spread: { homeLine: number | null; home: number | null; away: number | null };
  total: { line: number | null; over: number | null; under: number | null };
  marketCount: number;
}

export interface BasketballBoardState {
  loading: boolean;
  error: string | null;
  games: BballGame[];
  refetch: () => void;
}

function shape(raw: RawGame): BballGame {
  const ml = { home: null as number | null, away: null as number | null };
  const spread = { homeLine: null as number | null, home: null as number | null, away: null as number | null };
  const total = { line: null as number | null, over: null as number | null, under: null as number | null };
  for (const m of raw.markets ?? []) {
    const c = m.canonical_code ?? "";
    if (c === "moneyline_home") ml.home = m.odds;
    else if (c === "moneyline_away") ml.away = m.odds;
    else if (c.startsWith("spread_home_")) { spread.homeLine = m.line_value; spread.home = m.odds; }
    else if (c.startsWith("spread_away_")) { spread.away = m.odds; }
    else if (c.startsWith("total_points_over_")) { total.line = m.line_value; total.over = m.odds; }
    else if (c.startsWith("total_points_under_")) { total.line = m.line_value; total.under = m.odds; }
  }
  return {
    fixtureId: raw.fixture_id,
    home: raw.home_team ?? "Home",
    away: raw.away_team ?? "Away",
    league: raw.league,
    tipoffAt: raw.tipoff_at,
    moneyline: ml,
    spread,
    total,
    marketCount: (raw.markets ?? []).length,
  };
}

export function useBasketballBoard(): BasketballBoardState {
  // Live odds surface: poll every 60s on top of return/focus refresh.
  const q = useRpcQuery<RawGame[] | null>("get_basketball_board", { refetchInterval: 60_000 });
  const games = useMemo<BballGame[]>(() => (Array.isArray(q.data) ? q.data.map(shape) : []), [q.data]);
  return useMemo(
    () => ({ loading: q.isLoading, error: q.error ? q.error.message : null, games, refetch: () => { void q.refetch(); } }),
    [q.isLoading, q.error, games, q.refetch],
  );
}
